<template>
    <card class="chart-container">
        <template v-slot:header>
            <h4 v-if="$slots.title || title" class="card-title">
                <slot name="title">{{ title }}</slot>
            </h4>
            <p class="card-category" v-if="$slots.subTitle || subTitle">
                <slot name="subTitle">{{ subTitle }}</slot>
            </p>
        </template>
        <div class="chart-wrapper">
            <div class="chart-area" :style="{ height: chartHeight }">
                <div v-if="loading" class="chart-loading-overlay">
                    <i class="fa fa-spinner fa-spin"></i>
                </div>
                <Bar
                    v-if="chartType === 'bar'"
                    :data="processedChartData"
                    :options="mergedOptions"
                    :height="chartHeightValue"
                    width="auto"
                />
                <Line
                    v-if="chartType === 'line'"
                    :data="processedChartData"
                    :options="mergedOptions"
                    :height="chartHeightValue"
                />
                <Pie
                    v-if="chartType === 'pie'"
                    :data="processedChartData"
                    :options="mergedOptions"
                    :height="chartHeightValue"
                    width="auto"
                />
            </div>
            <div class="footer">
                <div class="chart-legend">
                    <slot name="legend"></slot>
                </div>
                <hr />
                <div
                    class="stats d-flex align-items-center justify-content-between w-100"
                >
                    <div class="footer-content">
                        <slot name="footer"></slot>
                    </div>
                    <div v-if="infoText" class="info-tooltip" :title="infoText">
                        <i class="fa fa-info-circle"></i>
                    </div>
                </div>
                <div class="pull-right"></div>
            </div>
            <div
                v-if="showRawDataToggle"
                class="raw-data-toggle raw-data-block w-100 mt-2"
            >
                <div class="raw-data-header">
                    <button
                        type="button"
                        class="btn btn-sm btn-outline-primary raw-data-btn w-100"
                        @click="showRawData = !showRawData"
                    >
                        <span class="raw-data-label">
                            {{
                                showRawData
                                    ? $t('chart.chart_btn__hide_raw_data')
                                    : $t('chart.chart_btn__show_raw_data')
                            }}
                        </span>
                        <i
                            class="fa ml-1"
                            :class="
                                showRawData
                                    ? 'fa-chevron-up'
                                    : 'fa-chevron-down'
                            "
                        ></i>
                    </button>
                </div>
                <transition name="raw-data-slide">
                    <div v-if="showRawData" class="raw-data-panel mt-2">
                        <div
                            class="raw-data-header-actions d-flex justify-content-end mb-2"
                        >
                            <button
                                v-if="hasRawData"
                                type="button"
                                class="btn btn-link btn-sm raw-data-download-btn"
                                :title="
                                    $t('chart.chart_btn__download_raw_data')
                                "
                                @click.stop="downloadRawData"
                            >
                                <i class="fa fa-download"></i>
                            </button>
                        </div>
                        <div class="raw-data-scroll">
                            <table class="table table-sm table-striped mb-0">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th
                                            v-for="(
                                                label, colIndex
                                            ) in tableLabels"
                                            :key="colIndex"
                                        >
                                            {{ label }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template
                                        v-for="(ds, dsIndex) in tableDatasets"
                                        :key="dsIndex"
                                    >
                                        <tr>
                                            <td>
                                                {{
                                                    datasetHeaders[dsIndex] ||
                                                    title
                                                }}
                                            </td>
                                            <td
                                                v-for="(
                                                    label, colIndex
                                                ) in tableLabels"
                                                :key="colIndex"
                                            >
                                                {{
                                                    getValueWithPercent(
                                                        datasetHeaders[
                                                            dsIndex
                                                        ] || title,
                                                        label,
                                                        ds,
                                                        colIndex
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </card>
</template>

<script>
import { defineComponent, computed, ref } from 'vue';
import Card from './Card.vue';
import { Bar, Line, Pie } from 'vue-chartjs';
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    LineElement,
    ArcElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineController,
    BarController,
    PieController,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

const DoughnutCenterText = {
    id: 'doughnutCenterText',
    afterDraw(chart, args, options) {
        if (!options || !options.text) {
            return;
        }

        const chartType = chart.config.type;
        if (chartType !== 'pie' && chartType !== 'doughnut') {
            return;
        }

        const { ctx, chartArea } = chart;
        if (!chartArea) {
            return;
        }

        const rawText =
            typeof options.text === 'function'
                ? options.text({ chart, data: chart.data })
                : options.text;

        if (!rawText) {
            return;
        }

        const lines = Array.isArray(rawText)
            ? rawText
            : String(rawText).split('\n');

        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;

        ctx.save();
        const fontSize = options.fontSize || 14;
        const fontFamily = options.fontFamily || 'Helvetica, Arial, sans-serif';
        const fontStyle = options.fontStyle || '600';
        ctx.font = `${fontStyle} ${fontSize}px ${fontFamily}`;
        ctx.fillStyle = options.color || '#2c3e50';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const lineHeight = fontSize + (options.lineSpacing || 2);
        const totalHeight = lineHeight * (lines.length - 1);
        let offsetY = centerY - totalHeight / 2;

        lines.forEach(line => {
            ctx.fillText(line, centerX, offsetY);
            offsetY += lineHeight;
        });
        ctx.restore();
    },
};

ChartJS.register(
    Title,
    Tooltip,
    Legend,
    BarElement,
    LineElement,
    ArcElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineController,
    BarController,
    PieController,
    DoughnutCenterText,
    ChartDataLabels
);

export default defineComponent({
    name: 'ChartCard',
    components: {
        Card,
        Bar,
        Line,
        Pie,
    },
    props: {
        footerText: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            default: '',
        },
        subTitle: {
            type: String,
            default: '',
        },
        chartType: {
            type: String,
            default: 'line', // line | pie | bar
        },
        chartOptions: {
            type: Object,
            default: () => ({}),
        },
        chartData: {
            type: Object,
            default: () => ({
                labels: [],
                datasets: [],
            }),
        },
        chartPercentages: {
            type: Array,
            default: null,
        },
        chartHeight: {
            type: String,
            default: '400px', // Default height
        },
        chartColors: {
            type: Array,
            default: () => [],
        },
        infoText: {
            type: String,
            default: '',
        },
        showRawDataToggle: {
            type: Boolean,
            default: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        rawDataShowPercent: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        const showRawData = ref(false);

        const isPieChart = computed(
            () => String(props.chartType).toLowerCase() === 'pie'
        );

        const chartHeightValue = computed(() =>
            parseInt(props.chartHeight.replace('px', ''))
        );

        const processedChartData = computed(() => {
            const baseChartData =
                props.chartData && typeof props.chartData === 'object'
                    ? props.chartData
                    : { labels: [], datasets: [] };

            const sourceDatasets = Array.isArray(baseChartData.datasets)
                ? baseChartData.datasets
                : [];

            const datasets = sourceDatasets.map(dataset => {
                const hasBackground =
                    (Array.isArray(dataset.backgroundColor) &&
                        dataset.backgroundColor.length) ||
                    typeof dataset.backgroundColor === 'string';
                const hasBorder =
                    (Array.isArray(dataset.borderColor) &&
                        dataset.borderColor.length) ||
                    typeof dataset.borderColor === 'string';

                // Preserve provided background colors; if border missing, derive
                if (hasBackground) {
                    return {
                        ...dataset,
                        borderColor: hasBorder
                            ? dataset.borderColor
                            : dataset.backgroundColor,
                        borderWidth:
                            typeof dataset.borderWidth === 'number'
                                ? dataset.borderWidth
                                : 1,
                    };
                }

                const backgroundColor = props.chartColors.slice(
                    0,
                    dataset.data.length
                );
                const borderColor = backgroundColor.map(color =>
                    color.replace('0.8', '1')
                );

                return {
                    ...dataset,
                    backgroundColor,
                    borderColor,
                    borderWidth: 1,
                };
            });

            return {
                ...baseChartData,
                datasets,
            };
        });

        const tableLabels = computed(() => {
            const labels = processedChartData.value.labels;
            return Array.isArray(labels) ? labels : [];
        });

        const tableDatasets = computed(() => {
            const datasets = processedChartData.value.datasets;
            return Array.isArray(datasets) ? datasets : [];
        });

        const datasetHeaders = computed(() =>
            tableDatasets.value.map(ds => ds.label)
        );

        const mergedOptions = computed(() => {
            const dataForLegend =
                processedChartData.value &&
                typeof processedChartData.value === 'object'
                    ? processedChartData.value
                    : { datasets: [] };

            const normalizedType = String(props.chartType).toLowerCase();
            const hasSingleDataset =
                dataForLegend.datasets && dataForLegend.datasets.length <= 1;
            // Pie charts still need a legend even with a single dataset to
            // explain each slice label.
            const hideLegend = normalizedType !== 'pie' && hasSingleDataset;

            const userPlugins = props.chartOptions.plugins || {};
            const userLegend = userPlugins.legend || {};
            const explicitLegendDisplay = userLegend.display;

            const baseLegendDisplay =
                typeof explicitLegendDisplay === 'boolean'
                    ? explicitLegendDisplay
                    : !hideLegend;

            const textColor = '#333333';
            const axisTextColor = '#555555';
            const gridColor = 'rgba(0, 0, 0, 0.05)';

            const legendPosition =
                typeof userLegend.position !== 'undefined'
                    ? userLegend.position
                    : props.chartType === 'line'
                    ? 'bottom'
                    : undefined;

            const basePlugins = {
                legend: {
                    ...userLegend,
                    display: baseLegendDisplay,
                    position: legendPosition,
                    labels: {
                        color: textColor,
                        ...(userLegend.labels || {}),
                    },
                },
            };

            const userDatalabels = userPlugins.datalabels || {};
            const userTooltip = userPlugins.tooltip || {};
            const userCallbacks = userTooltip.callbacks || {};
            const hasUserLabelCallback =
                typeof userCallbacks.label === 'function';

            if (props.chartType === 'line') {
                basePlugins.datalabels = {
                    color: userDatalabels.color || textColor,
                    anchor: userDatalabels.anchor || 'end',
                    align: userDatalabels.align || 'top',
                    offset:
                        typeof userDatalabels.offset === 'number'
                            ? userDatalabels.offset
                            : 4,
                    ...userDatalabels,
                };
            } else if (props.chartType === 'pie') {
                // 區塊上的文字預設顯示百分比，實際數值改由 tooltip 顯示
                basePlugins.datalabels = {
                    color: userDatalabels.color || '#ffffff',
                    anchor: userDatalabels.anchor || 'center',
                    align: userDatalabels.align || 'center',
                    formatter:
                        userDatalabels.formatter ||
                        ((value, ctx) => {
                            const dataset =
                                (ctx &&
                                    ctx.chart &&
                                    ctx.chart.data &&
                                    ctx.chart.data.datasets &&
                                    ctx.chart.data.datasets[
                                        ctx.datasetIndex
                                    ]) ||
                                {};
                            const data =
                                (dataset &&
                                    Array.isArray(dataset.data) &&
                                    dataset.data) ||
                                [];
                            const numericValue = Number(value) || 0;
                            const total = data.reduce(
                                (sum, v) => sum + (Number(v) || 0),
                                0
                            );
                            const ratio =
                                total > 0
                                    ? ((numericValue / total) * 100).toFixed(1)
                                    : '0.0';
                            return `${ratio}%`;
                        }),
                    ...userDatalabels,
                };

                basePlugins.tooltip = {
                    ...userTooltip,
                    callbacks: {
                        ...userCallbacks,
                        label: hasUserLabelCallback
                            ? userCallbacks.label
                            : context => {
                                  const dataset = context.dataset || {};
                                  const data = (dataset && dataset.data) || [];

                                  const value = Number(context.raw) || 0;
                                  const total = data.reduce(
                                      (sum, v) => sum + (Number(v) || 0),
                                      0
                                  );
                                  const ratio =
                                      total > 0
                                          ? ((value / total) * 100).toFixed(1)
                                          : '0.0';

                                  const label = context.label || '';
                                  return label
                                      ? `${label}: ${value} (${ratio}%)`
                                      : `${value} (${ratio}%)`;
                              },
                    },
                };
            }

            // Default tooltip label: show value only (omit dataset key) unless user overrides
            if (!basePlugins.tooltip) {
                basePlugins.tooltip = {
                    ...userTooltip,
                    callbacks: {
                        ...userCallbacks,
                        label: hasUserLabelCallback
                            ? userCallbacks.label
                            : context => {
                                  const value =
                                      context.formattedValue ??
                                      (context.parsed &&
                                      typeof context.parsed.y !== 'undefined'
                                          ? context.parsed.y
                                          : context.parsed) ??
                                      context.raw ??
                                      '';
                                  return `${value}`;
                              },
                    },
                };
            }

            const userScales = props.chartOptions.scales || {};

            // Pie / doughnut 圖不需要 Cartesian x、y 軸，
            // 只保留使用者自己在 options 裡設定的 radial scale（例如 r）。
            const baseScales =
                props.chartType === 'pie'
                    ? userScales
                    : (() => {
                          const xScaleUser = userScales.x || {};
                          const yScaleUser = userScales.y || {};

                          const xTicksUser = xScaleUser.ticks || {};
                          const yTicksUser = yScaleUser.ticks || {};
                          const xGridUser = xScaleUser.grid || {};
                          const yGridUser = yScaleUser.grid || {};

                          return {
                              x: {
                                  ...xScaleUser,
                                  ticks: {
                                      color: xTicksUser.color || axisTextColor,
                                      ...xTicksUser,
                                  },
                                  grid: {
                                      color: xGridUser.color || gridColor,
                                      ...xGridUser,
                                  },
                              },
                              y: {
                                  ...yScaleUser,
                                  ticks: {
                                      color: yTicksUser.color || axisTextColor,
                                      ...yTicksUser,
                                  },
                                  grid: {
                                      color: yGridUser.color || gridColor,
                                      ...yGridUser,
                                  },
                              },
                          };
                      })();

            const baseIndexAxis =
                typeof props.chartOptions.indexAxis !== 'undefined'
                    ? props.chartOptions.indexAxis
                    : 'x';

            // Add top padding to reduce label clipping (especially line charts with datalabels)
            const baseLayout = {
                padding: {
                    top:
                        (props.chartOptions.layout &&
                            props.chartOptions.layout.padding &&
                            props.chartOptions.layout.padding.top) ||
                        12,
                },
            };

            // Default cutout to make pie charts appear as doughnut
            const cutoutValue =
                normalizedType === 'pie'
                    ? props.chartOptions.cutout || '60%'
                    : props.chartOptions.cutout;

            return {
                ...props.chartOptions,
                maintainAspectRatio: false,
                indexAxis: baseIndexAxis,
                layout: {
                    ...baseLayout,
                    ...(props.chartOptions.layout || {}),
                },
                cutout: cutoutValue,
                // 對於 pie/doughnut，直接使用 baseScales（即使用者原本的 scales），
                // 避免強制產生 x、y 軸；其他圖則照原本邏輯給 x、y。
                scales: {
                    ...baseScales,
                },
                plugins: {
                    ...basePlugins,
                    ...userPlugins,
                    legend: {
                        ...basePlugins.legend,
                        ...userPlugins.legend,
                        display: baseLegendDisplay,
                    },
                },
            };
        });

        const formatValue = value => {
            if (value === null || value === undefined) {
                return '-';
            }
            if (typeof value === 'number' && Number.isNaN(value)) {
                return '-';
            }
            return value;
        };

        const formatPercent = (value, allData) => {
            if (value === null || value === undefined) {
                return '-';
            }

            const numericValue = Number(value);
            if (Number.isNaN(numericValue)) {
                return '-';
            }

            const dataArray = Array.isArray(allData) ? allData : [];
            const total = dataArray.reduce(
                (sum, v) => sum + (Number(v) || 0),
                0
            );

            if (total <= 0) {
                return '0.0%';
            }

            const ratio = (numericValue / total) * 100;
            return `${ratio.toFixed(1)}%`;
        };

        const hasRawData = computed(
            () =>
                tableLabels.value.length > 0 &&
                tableDatasets.value.some(
                    ds => Array.isArray(ds.data) && ds.data.length > 0
                )
        );

        const percentagesByLabel = computed(() => {
            const entries =
                props.chartPercentages || props.chartData?.percentages;
            if (!Array.isArray(entries)) return null;
            const map = new Map();
            entries.forEach(entry => {
                const resultMap = new Map();
                (entry.results || []).forEach(r =>
                    resultMap.set(r.result, Number(r.pct) || 0)
                );
                map.set(entry.label, resultMap);
            });
            return map;
        });

        const hasDatasetPercentages = computed(() => {
            if (percentagesByLabel.value && percentagesByLabel.value.size > 0) {
                return true;
            }
            return Boolean(props.rawDataShowPercent);
        });

        const getPercentDisplay = (datasetLabel, label, ds, colIndex) => {
            const pctMap = percentagesByLabel.value;
            if (pctMap) {
                const resMap = pctMap.get(label);
                if (resMap && resMap.has(datasetLabel)) {
                    const pct = Number(resMap.get(datasetLabel)) || 0;
                    return `${pct.toFixed(1)}%`;
                }
            }
            const value =
                ds.data && ds.data[colIndex] !== undefined
                    ? ds.data[colIndex]
                    : null;
            return formatPercent(value, ds.data);
        };

        const getValueWithPercent = (datasetLabel, label, ds, colIndex) => {
            const rawValue =
                ds.data && ds.data[colIndex] !== undefined
                    ? ds.data[colIndex]
                    : null;
            const valueText = formatValue(rawValue);
            if (!hasDatasetPercentages.value) {
                return valueText;
            }
            const pctText = getPercentDisplay(
                datasetLabel,
                label,
                ds,
                colIndex
            );
            return pctText ? `${valueText} (${pctText})` : valueText;
        };

        const rawDataRows = computed(() => {
            const headerRow = [''].concat(tableLabels.value);
            const rows = [];

            tableDatasets.value.forEach((ds, dsIndex) => {
                const baseLabel = datasetHeaders.value[dsIndex];

                rows.push([
                    baseLabel,
                    ...tableLabels.value.map((label, colIndex) =>
                        hasDatasetPercentages.value
                            ? getValueWithPercent(
                                  baseLabel,
                                  label,
                                  ds,
                                  colIndex
                              )
                            : formatValue(
                                  ds.data && ds.data[colIndex] !== undefined
                                      ? ds.data[colIndex]
                                      : null
                              )
                    ),
                ]);
            });

            return [headerRow, ...rows];
        });

        const toCsv = rows =>
            rows
                .map(row =>
                    row
                        .map(value => {
                            if (value === null || value === undefined) {
                                return '';
                            }
                            const str = String(value);
                            const escaped = str.replace(/"/g, '""');
                            return /[",\n]/.test(str)
                                ? `"${escaped}"`
                                : escaped;
                        })
                        .join(',')
                )
                .join('\n');

        const sanitizeFileName = name => {
            const fallback = 'raw-data';
            const base = (name || fallback).toString().trim() || fallback;
            return base.replace(/[^a-z0-9\\-_]+/gi, '_');
        };

        const downloadRawData = () => {
            if (!hasRawData.value) {
                return;
            }
            const csvContent = toCsv(rawDataRows.value);
            const blob = new Blob([csvContent], {
                type: 'text/csv;charset=utf-8;',
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${sanitizeFileName(props.title)}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        };

        return {
            showRawData,
            isPieChart,
            chartHeightValue,
            processedChartData,
            mergedOptions,
            tableLabels,
            tableDatasets,
            datasetHeaders,
            formatValue,
            formatPercent,
            hasRawData,
            downloadRawData,
            getPercentDisplay,
            hasDatasetPercentages,
            getValueWithPercent,
        };
    },
});
</script>

<style scoped>
.chart-container {
    display: flex;
}
.chart-wrapper {
    padding-top: 30px;
    padding-bottom: 30px;
    display: flex;
    flex-direction: column; /* Ensure the items are aligned in column direction */
    align-items: stretch;
    justify-content: flex-start;
    min-height: 320px; /* 基本高度，展開 raw data 時讓整張卡片往下撐高 */
    width: 100% !important; /* Ensure width is 100% to fit parent container */
    position: relative;
}

.chart-area {
    width: 100%;
    position: relative;
}
.footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-top: 10px;
    width: 100%;
}
.chart-legend,
.stats {
    width: 100%;
    color: rgb(53, 50, 50) !important;
}
.info-tooltip {
    cursor: pointer;
    color: #9a9a9a;
}

.info-tooltip:hover {
    color: #666;
}

.raw-data-toggle {
    margin-top: 10px;
}

.raw-data-header {
    display: flex;
    align-items: center;
    gap: 8px;
}

.raw-data-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    font-size: 0.8rem;
    padding: 4px 10px;
    border-radius: 4px;
    border-width: 1px;
    color: #666666;
    flex: 1;
}

.raw-data-download-btn {
    color: #666666;
    padding: 4px 6px;
}

.raw-data-download-btn:hover {
    color: #333333;
    text-decoration: none;
}

.raw-data-download-btn:focus {
    box-shadow: none;
}

.raw-data-panel {
    overflow: visible;
    width: 100%;
}

.raw-data-scroll {
    overflow: auto;
    width: 100%;
}

.chart-loading-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(255, 255, 255, 0.75);
    z-index: 2;
    font-size: 2rem;
    color: #999;
}

.raw-data-panel table {
    font-size: 0.8rem;
}

.raw-data-slide-enter-active,
.raw-data-slide-leave-active {
    transition: max-height 0.25s ease, opacity 0.2s ease, transform 0.2s ease;
}

.raw-data-slide-enter-from,
.raw-data-slide-leave-to {
    max-height: 0;
    opacity: 0;
    transform: translateY(-4px);
}

.raw-data-slide-enter-to,
.raw-data-slide-leave-from {
    max-height: 500px;
    opacity: 1;
    transform: translateY(0);
}

.raw-data-label {
    flex: 1;
    text-align: left;
}

.raw-data-block {
    border: 1px solid #e3e3e3;
    border-radius: 4px;
    background: #fafafa;
    overflow: hidden;
}
</style>
